import * as ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import {
  FiUsers,
  FiPlus,
  FiUser,
  FiChevronLeft,
  FiChevronRight,
  FiRefreshCw,
  FiX,
  FiMail,
  FiPhone,
  FiEdit,
  FiDownload,
  FiLock,
  FiGrid,
  FiList,
} from "react-icons/fi";
import {
  fetchULBUsers,
  createULBUser,
  updateULBUser,
  resetULBUserPassword,
} from "../../redux/actions/user-management/ulbUserActions";
import {
  fetchDistricts,
  fetchMunicipalities,
  clearMunicipalities,
} from "../../redux/actions/internal/internalActions";

const ULBUsers = () => {
  const dispatch = useDispatch();
  const { loading, users, pagination } = useSelector(
    (state) =>
      state.ulbUser || {
        loading: false,
        users: [],
        pagination: { page: 1, limit: 10, totalRecords: 0, totalPages: 0 },
      },
  );

  const { districts, municipalities } = useSelector(
    (state) => state.internal || { districts: [], municipalities: [] },
  );

  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    district_id: "",
    municipality_id: "",
  });
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [phoneError, setPhoneError] = useState("");
  const [editPhoneError, setEditPhoneError] = useState("");
  const [limit, setLimit] = useState(10);
  const limitOptions = [
    10, 20, 50, 100, 200, 500, 1000, 1500, 2000, 3000, 4000,
  ];
  const [viewMode, setViewMode] = useState("table");

  const [formData, setFormData] = useState({
    full_name: "",
    designation: "",
    username: "",
    email: "",
    phone: "",
    district_id: "",
    municipality_id: "",
  });

  const [passwordData, setPasswordData] = useState({
    newPassword: "",
    confirmPassword: "",
  });

  const validatePhone = (value) => {
    return value.replace(/\D/g, "").slice(0, 10);
  };

  const isValidPhone = (phone) => {
    return phone.length === 10;
  };

  useEffect(() => {
    dispatch(fetchULBUsers(currentPage, limit, filters));
    dispatch(fetchDistricts());
  }, [
    dispatch,
    currentPage,
    limit,
    filters.district_id,
    filters.municipality_id,
  ]);

  useEffect(() => {
    if (filters.district_id) {
      dispatch(fetchMunicipalities(filters.district_id));
    } else {
      dispatch(clearMunicipalities());
    }
  }, [filters.district_id, dispatch]);

  useEffect(() => {
    if (formData.district_id) {
      dispatch(fetchMunicipalities(formData.district_id));
    } else {
      dispatch(clearMunicipalities());
    }
  }, [formData.district_id, dispatch]);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= pagination.totalPages) {
      setCurrentPage(page);
      dispatch(fetchULBUsers(page, limit, filters));
    }
  };

  const handleRefresh = () => {
    dispatch(fetchULBUsers(currentPage, limit, filters));
    toast.success("Data refreshed!");
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData({ ...passwordData, [name]: value });
  };

  const resetForm = () => {
    setFormData({
      full_name: "",
      designation: "",
      username: "",
      email: "",
      phone: "",
      district_id: "",
      municipality_id: "",
    });
    setPasswordData({ newPassword: "", confirmPassword: "" });
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    if (!isValidPhone(formData.phone)) {
      toast.error("Phone number must be exactly 10 digits");
      return;
    }
    try {
      const result = await dispatch(createULBUser(formData));
      if (result?.meta?.status) {
        setShowAddModal(false);
        resetForm();
        dispatch(fetchULBUsers(currentPage, limit, filters));
      }
    } catch (error) {}
  };

  const handleEdit = (user) => {
    setSelectedUserId(user.id);
    setFormData({
      full_name: user.full_name || "",
      designation: user.designation || "",
      username: user.username || "",
      email: user.email || "",
      phone: user.phone || "",
      district_id: user.district_id || "",
      municipality_id: user.municipality_id || "",
    });
    if (user.district_id) {
      dispatch(fetchMunicipalities(user.district_id));
    }
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!isValidPhone(formData.phone)) {
      toast.error("Phone number must be exactly 10 digits");
      return;
    }
    try {
      const result = await dispatch(updateULBUser(selectedUserId, formData));
      if (result?.meta?.status) {
        setShowEditModal(false);
        resetForm();
        dispatch(fetchULBUsers(currentPage, limit, filters));
      }
    } catch (error) {}
  };

  const handlePasswordReset = (user) => {
    setSelectedUserId(user.id);
    setPasswordData({ newPassword: "", confirmPassword: "" });
    setShowPasswordModal(true);
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    try {
      const result = await dispatch(resetULBUserPassword(selectedUserId));
      if (result?.meta?.status) {
        setShowPasswordModal(false);
        setPasswordData({ newPassword: "", confirmPassword: "" });
      }
    } catch (error) {}
  };

  const handleExportToExcel = async () => {
    try {
      const toastId = toast.loading("Preparing ULB Users Excel...");

      const result = await dispatch(fetchULBUsers(1, 99999, filters));

      if (!result?.data || result.data.length === 0) {
        toast.error("No data to export");
        toast.dismiss(toastId);
        return;
      }

      const allUsers = result.data;

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("ULB Users");

      const columns = [
        { header: "ID", width: 10 },
        { header: "Full Name", width: 25 },
        { header: "Username", width: 20 },
        { header: "Designation", width: 20 },
        { header: "Email", width: 30 },
        { header: "Phone", width: 15 },
        { header: "Role", width: 20 },
        { header: "District", width: 25 },
        { header: "Municipality", width: 25 },
      ];

      const headerRow = worksheet.addRow(columns.map((col) => col.header));
      headerRow.height = 25;
      headerRow.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 12 };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFCC6633" },
        };
        cell.alignment = {
          vertical: "middle",
          horizontal: "center",
          wrapText: true,
        };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });

      allUsers.forEach((item, index) => {
        const rowArray = [
          item.id || "N/A",
          item.full_name || "N/A",
          item.username || "N/A",
          item.designation || "N/A",
          item.email || "N/A",
          item.phone || "N/A",
          item.role || "N/A",

          item.district_name || "N/A",
          item.municipality_name || "N/A",
        ];
        const row = worksheet.addRow(rowArray);
        row.eachCell((cell) => {
          cell.alignment = { vertical: "middle", horizontal: "left" };
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
          if (index % 2 === 1) {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "FFF0F8FF" },
            };
          } else {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "FFFFFFFF" },
            };
          }
        });
      });

      worksheet.columns = columns.map((col) => ({ width: col.width }));
      worksheet.views = [{ state: "frozen", ySplit: 1 }];

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const date = new Date();
      const dateStr = `${String(date.getDate()).padStart(2, "0")}-${String(date.getMonth() + 1).padStart(2, "0")}-${date.getFullYear()}`;
      saveAs(blob, `ULB_Users_${dateStr}.xlsx`);

      toast.success(`Exported ${allUsers.length} records successfully!`, {
        id: toastId,
      });
    } catch (error) {
      toast.error("Failed to export data");
      console.error(error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 p-4 md:p-6">
      <div className="fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-orange-200/30 dark:bg-orange-900/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-200/30 dark:bg-blue-900/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="relative backdrop-blur-xl bg-white/70 dark:bg-slate-800/70 rounded-2xl shadow-xl border border-white/20 dark:border-slate-700/50 p-4 md:p-5 mb-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl shadow-lg">
              <FiUsers className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-orange-600 to-orange-400 bg-clip-text text-transparent">
                ULB-Level Users
              </h1>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                Manage ULB level administrators
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={handleRefresh}
              className="flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-slate-700 hover:bg-slate-50 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 border border-slate-200 dark:border-slate-600 text-base"
            >
              <FiRefreshCw className="w-4 h-4" />
              <span className="hidden sm:inline">Refresh</span>
            </button>
            <button
              onClick={handleExportToExcel}
              className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 text-base"
            >
              <FiDownload className="w-4 h-4" />
              <span className="hidden sm:inline">Export</span>
            </button>
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 text-base"
            >
              <FiPlus className="w-4 h-4" />
              <span>Add User</span>
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-5">
        {[
          {
            label: "Total Users",
            value: pagination.totalRecords || 0,
            icon: FiUsers,
            bgColor: "bg-orange-50 dark:bg-orange-900/20",
            borderColor: "border-orange-200 dark:border-orange-800",
            iconBg: "from-orange-500 to-orange-600",
          },
          {
            label: "Districts",
            value: new Set(users.map((o) => o.district_id)).size,
            icon: FiUsers,
            bgColor: "bg-blue-50 dark:bg-blue-900/20",
            borderColor: "border-blue-200 dark:border-blue-800",
            iconBg: "from-blue-500 to-blue-600",
          },
          {
            label: "Municipalities",
            value: new Set(users.map((o) => o.municipality_id)).size,
            icon: FiUsers,
            bgColor: "bg-purple-50 dark:bg-purple-900/20",
            borderColor: "border-purple-200 dark:border-purple-800",
            iconBg: "from-purple-500 to-purple-600",
          },
        ].map((stat, idx) => (
          <div
            key={idx}
            className={`relative ${stat.bgColor} border ${stat.borderColor} rounded-xl shadow-sm p-3 hover:shadow-xl transition-all duration-300 transform hover:scale-105`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 dark:text-slate-300">
                  {stat.label}
                </p>
                <p className="text-2xl font-bold text-slate-800 dark:text-white mt-0.5">
                  {stat.value}
                </p>
              </div>
              <div
                className={`p-2.5 bg-gradient-to-br ${stat.iconBg} rounded-xl shadow-lg`}
              >
                <stat.icon className="w-4 h-4 text-white" />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="relative backdrop-blur-xl bg-white/70 dark:bg-slate-800/70 rounded-2xl shadow-xl border border-white/20 dark:border-slate-700/50 p-4">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-3 mb-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full lg:w-auto">
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <select
                value={filters.district_id}
                onChange={(e) => {
                  const value = e.target.value;
                  setFilters({
                    district_id: value,
                    municipality_id: "",
                  });
                  setCurrentPage(1);
                }}
                className="px-3 py-2 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm text-slate-700 dark:text-white w-full sm:w-auto"
              >
                <option value="">All Districts</option>
                {districts.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
              <select
                value={filters.municipality_id}
                onChange={(e) => {
                  setFilters({
                    ...filters,
                    municipality_id: e.target.value,
                  });
                  setCurrentPage(1);
                }}
                disabled={!filters.district_id}
                className="px-3 py-2 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm text-slate-700 dark:text-white disabled:opacity-50 w-full sm:w-auto min-w-[180px]"
              >
                <option value="">All Municipalities</option>
                {municipalities.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
              {(filters.district_id || filters.municipality_id) && (
                <button
                  onClick={() => {
                    setFilters({
                      district_id: "",
                      municipality_id: "",
                    });
                    setCurrentPage(1);
                    dispatch(clearMunicipalities());
                  }}
                  className="flex items-center gap-1.5 px-3 py-2 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-xl shadow-md hover:shadow-lg transition-all duration-200 transform hover:scale-105 text-sm font-medium whitespace-nowrap"
                >
                  <FiX className="w-3.5 h-3.5" />
                  Clear
                </button>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3 w-full lg:w-auto">
            <div className="flex items-center gap-2">
              <label className="text-sm text-slate-600 dark:text-slate-400">
                Show:
              </label>
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="px-3 py-2 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm text-slate-700 dark:text-white"
              >
                {limitOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="relative">
              <div className="w-12 h-12 border-4 border-orange-200 dark:border-orange-800 rounded-full"></div>
              <div className="absolute top-0 left-0 w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          </div>
        ) : users.length > 0 ? (
          <>
            <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-700">
              <table className="w-full">
                <thead className="bg-slate-50 dark:bg-slate-700/50">
                  <tr>
                    <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                      ID
                    </th>
                    <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider min-w-[150px]">
                      Full Name
                    </th>
                    <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                      Username
                    </th>
                    <th className="text-center py-2.5 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                      Designation
                    </th>
                    <th className="text-center py-2.5 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="text-center py-2.5 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                      Phone
                    </th>
                    <th className="text-center py-2.5 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                      District
                    </th>
                    <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                      Municipality
                    </th>
                    <th className="text-center py-2.5 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                  {users.map((item, index) => (
                    <tr
                      key={item.id}
                      className="hover:bg-orange-50/50 dark:hover:bg-orange-900/20 transition-colors duration-150"
                    >
                      <td className="py-2.5 px-3 text-sm font-mono text-orange-600 dark:text-orange-400">
                        {item.id}
                      </td>
                      <td className="py-2.5 px-3">
                        <div className="flex items-center gap-2.5">
                          <div className="w-7 h-7 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center text-white">
                            <FiUser className="w-3.5 h-3.5" />
                          </div>
                          <span className="text-sm font-medium text-slate-800 dark:text-white">
                            {item.full_name || "N/A"}
                          </span>
                        </div>
                      </td>
                      <td className="py-2.5 px-3 text-sm text-orange-600 dark:text-orange-400 italic">
                        @{item.username || "N/A"}
                      </td>
                      <td className="py-2.5 px-3 text-sm text-slate-600 dark:text-slate-300">
                        {item.designation || "N/A"}
                      </td>
                      <td className="py-2.5 px-3">
                        <div className="flex items-center gap-2">
                          <FiMail className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500" />
                          <span className="text-sm text-slate-600 dark:text-slate-300">
                            {item.email || "N/A"}
                          </span>
                        </div>
                      </td>
                      <td className="py-2.5 px-3">
                        <div className="flex items-center gap-2">
                          <FiPhone className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500" />
                          <span className="text-sm text-slate-600 dark:text-slate-300">
                            {item.phone || "N/A"}
                          </span>
                        </div>
                      </td>
                      <td className="py-2.5 px-3">
                        <span className="inline-block px-2.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-full text-xs font-medium">
                          {item.role || "N/A"}
                        </span>
                      </td>
                      <td className="py-2.5 px-3 text-sm text-slate-600 dark:text-slate-300">
                        {item.district_name || "N/A"}
                      </td>
                      <td className="py-2.5 px-3 text-sm text-slate-600 dark:text-slate-300">
                        {item.municipality_name || "N/A"}
                      </td>
                      <td className="py-2.5 px-3">
                        <div className="flex items-center justify-center gap-1.5">
                          <button
                            onClick={() => handleEdit(item)}
                            className="p-1.5 text-orange-600 hover:bg-orange-100 dark:text-orange-400 dark:hover:bg-orange-900/30 rounded-lg transition-all duration-200 hover:scale-110"
                            title="Edit User"
                          >
                            <FiEdit className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => handlePasswordReset(item)}
                            className="p-1.5 text-blue-600 hover:bg-blue-100 dark:text-blue-400 dark:hover:bg-blue-900/30 rounded-lg transition-all duration-200 hover:scale-110"
                            title="Reset Password"
                          >
                            <FiLock className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pagination.totalPages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between mt-4 pt-3 border-t border-slate-200 dark:border-slate-700 gap-3">
                <div className="text-sm text-slate-500 dark:text-slate-400">
                  Showing {(pagination.page - 1) * pagination.limit + 1} to{" "}
                  {Math.min(
                    pagination.page * pagination.limit,
                    pagination.totalRecords,
                  )}{" "}
                  of {pagination.totalRecords} entries
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="p-2 rounded-xl bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 text-slate-600 dark:text-slate-300"
                  >
                    <FiChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="px-4 py-1.5 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl text-sm font-medium min-w-[40px] text-center">
                    {currentPage}
                  </span>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === pagination.totalPages}
                    className="p-2 rounded-xl bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 text-slate-600 dark:text-slate-300"
                  >
                    <FiChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12">
            <div className="w-20 h-20 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-3">
              <FiUsers className="w-10 h-10 text-slate-300 dark:text-slate-500" />
            </div>
            <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-300">
              No ULB users found
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              Get started by adding your first ULB user
            </p>
          </div>
        )}
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-md">
          <div className="bg-white dark:bg-slate-800 rounded-2xl p-5 max-w-2xl w-full mx-4 shadow-2xl max-h-[90vh] overflow-y-auto animate-fadeIn">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-slate-800 dark:text-white">
                Add ULB User
              </h3>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  resetForm();
                }}
                className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                <FiX className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            <form onSubmit={handleCreateSubmit} className="space-y-3.5">
              <div className="grid grid-cols-2 gap-3.5">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-slate-700 dark:text-white transition-all duration-200"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Designation *
                  </label>
                  <input
                    type="text"
                    name="designation"
                    value={formData.designation}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-slate-700 dark:text-white transition-all duration-200"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3.5">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Username *
                  </label>
                  <input
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-slate-700 dark:text-white transition-all duration-200"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-slate-700 dark:text-white transition-all duration-200"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Phone *
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={(e) => {
                    const value = e.target.value
                      .replace(/\D/g, "")
                      .slice(0, 10);
                    setFormData({ ...formData, phone: value });
                    if (value.length > 0 && value.length !== 10) {
                      setEditPhoneError(
                        "Phone number must be exactly 10 digits",
                      );
                    } else {
                      setEditPhoneError("");
                    }
                  }}
                  required
                  className={`w-full px-3 py-2 bg-slate-50 dark:bg-slate-700 border rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-slate-700 dark:text-white transition-all duration-200 ${
                    editPhoneError
                      ? "border-red-500 dark:border-red-500"
                      : "border-slate-200 dark:border-slate-600"
                  }`}
                  placeholder="Enter 10-digit phone number"
                />
                {editPhoneError && (
                  <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                    {editPhoneError}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3.5">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    District *
                  </label>
                  <select
                    name="district_id"
                    value={formData.district_id}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-slate-700 dark:text-white transition-all duration-200"
                  >
                    <option value="">Select District</option>
                    {districts.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Municipality *
                  </label>
                  <select
                    name="municipality_id"
                    value={formData.municipality_id}
                    onChange={handleInputChange}
                    required
                    disabled={!formData.district_id}
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-slate-700 dark:text-white disabled:opacity-50 transition-all duration-200"
                  >
                    <option value="">Select Municipality</option>
                    {municipalities.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-3.5 border-t border-slate-200 dark:border-slate-700">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-600 transition-all duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
                >
                  Create User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-md">
          <div className="bg-white dark:bg-slate-800 rounded-2xl p-5 max-w-2xl w-full mx-4 shadow-2xl max-h-[90vh] overflow-y-auto animate-fadeIn">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-slate-800 dark:text-white">
                Edit ULB User
              </h3>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  resetForm();
                }}
                className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                <FiX className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            <form onSubmit={handleEditSubmit} className="space-y-3.5">
              <div className="grid grid-cols-2 gap-3.5">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-slate-700 dark:text-white transition-all duration-200"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Designation *
                  </label>
                  <input
                    type="text"
                    name="designation"
                    value={formData.designation}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-slate-700 dark:text-white transition-all duration-200"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3.5">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Username *
                  </label>
                  <input
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-slate-700 dark:text-white transition-all duration-200"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-slate-700 dark:text-white transition-all duration-200"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Phone *
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={(e) => {
                    const value = e.target.value
                      .replace(/\D/g, "")
                      .slice(0, 10);
                    setFormData({ ...formData, phone: value });
                    if (value.length > 0 && value.length !== 10) {
                      setPhoneError("Phone number must be exactly 10 digits");
                    } else {
                      setPhoneError("");
                    }
                  }}
                  required
                  className={`w-full px-3 py-2 bg-slate-50 dark:bg-slate-700 border rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-slate-700 dark:text-white transition-all duration-200 ${
                    phoneError
                      ? "border-red-500 dark:border-red-500"
                      : "border-slate-200 dark:border-slate-600"
                  }`}
                  placeholder="Enter 10-digit phone number"
                />
                {phoneError && (
                  <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                    {phoneError}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3.5">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    District *
                  </label>
                  <select
                    name="district_id"
                    value={formData.district_id}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-slate-700 dark:text-white transition-all duration-200"
                  >
                    <option value="">Select District</option>
                    {districts.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Municipality *
                  </label>
                  <select
                    name="municipality_id"
                    value={formData.municipality_id}
                    onChange={handleInputChange}
                    required
                    disabled={!formData.district_id}
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-slate-700 dark:text-white disabled:opacity-50 transition-all duration-200"
                  >
                    <option value="">Select Municipality</option>
                    {municipalities.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-3.5 border-t border-slate-200 dark:border-slate-700">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-600 transition-all duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
                >
                  Update User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-md">
          <div className="bg-white dark:bg-slate-800 rounded-2xl p-5 max-w-md w-full mx-4 shadow-2xl animate-fadeIn">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-slate-800 dark:text-white">
                Reset Password
              </h3>
              <button
                onClick={() => {
                  setShowPasswordModal(false);
                  setPasswordData({ newPassword: "", confirmPassword: "" });
                }}
                className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                <FiX className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            <div className="text-center py-3">
              <p className="text-slate-600 dark:text-slate-300 mb-2">
                Are you sure you want to reset the password for this user?
              </p>
            </div>

            <div className="flex justify-end gap-3 pt-3.5 border-t border-slate-200 dark:border-slate-700">
              <button
                type="button"
                onClick={() => {
                  setShowPasswordModal(false);
                  setPasswordData({ newPassword: "", confirmPassword: "" });
                }}
                className="px-4 py-2 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-600 transition-all duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handlePasswordSubmit}
                className="px-5 py-2 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
              >
                Reset Password
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ULBUsers;
